import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/api';
import { ConferenceRoom, Reservation, ReservationFormData, ViewMode } from './types';
import { DAY_END_HOUR, DAY_START_HOUR, HOUR_ROW_HEIGHT } from './stiklainisStyles';
import { getNext30MinSlotStart, getNextAvailableSlot, getWeekDays } from './slotUtils';
import {
  fetchAllReservationsInRange,
  getMonthGridRange,
  getWeekRangeBounds,
} from './fetchReservations';
import { recurringEndFromWeeks } from './ReservationFormDialog';

interface UseStiklainisCalendarOptions {
  selectedRoom?: string;
  onRoomChange?: (roomId: string) => void;
  onRoomsLoaded?: (rooms: ConferenceRoom[]) => void;
}

export function useStiklainisCalendar({ selectedRoom, onRoomChange, onRoomsLoaded }: UseStiklainisCalendarOptions) {
  const { t } = useTranslation();
  const { user, token, hasPermission } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [rooms, setRooms] = useState<ConferenceRoom[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [reservationDialogNotice, setReservationDialogNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoReservation, setInfoReservation] = useState<Reservation | null>(null);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<HTMLElement | null>(null);
  const [calendarMenuReservation, setCalendarMenuReservation] = useState<Reservation | null>(null);
  const [dayAgendaDate, setDayAgendaDate] = useState<Date | null>(null);
  const [selectedMobileDay, setSelectedMobileDay] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const emptyForm = (): ReservationFormData => ({
    title: '',
    description: '',
    startDate: new Date(),
    startTime: new Date(),
    endTime: new Date(),
    roomId: selectedRoom || '',
    attendeeCount: '',
    isRecurring: false,
    recurringPattern: 'weekly',
    recurringWeeks: 4,
  });
  const [formData, setFormData] = useState<ReservationFormData>(emptyForm);
  const [deleteSeriesPrompt, setDeleteSeriesPrompt] = useState<{
    open: boolean;
    reservationId: string | null;
  }>({ open: false, reservationId: null });

  const reservationsAbortRef = useRef<AbortController | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const onRoomChangeRef = useRef(onRoomChange);
  const onRoomsLoadedRef = useRef(onRoomsLoaded);
  onRoomChangeRef.current = onRoomChange;
  onRoomsLoadedRef.current = onRoomsLoaded;

  const monthNames = useMemo(() => [
    t('stiklainis.january'), t('stiklainis.february'), t('stiklainis.march'), t('stiklainis.april'),
    t('stiklainis.may'), t('stiklainis.june'), t('stiklainis.july'), t('stiklainis.august'),
    t('stiklainis.september'), t('stiklainis.october'), t('stiklainis.november'), t('stiklainis.december')
  ], [t]);

  const dayNames = useMemo(() => [
    t('stiklainis.monday'), t('stiklainis.tuesday'), t('stiklainis.wednesday'),
    t('stiklainis.thursday'), t('stiklainis.friday'), t('stiklainis.saturday'), t('stiklainis.sunday')
  ], [t]);

  const effectiveRoomId = selectedRoom || rooms[0]?.id || '';

  const loadData = useCallback(async () => {
    try {
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
      }
      setError(null);

      if (!token) {
        setError(t('stiklainis.notLoggedIn'));
        setLoading(false);
        return;
      }

      const roomsResponse = await fetch(getApiUrl('/conference-rooms'), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!roomsResponse.ok) {
        throw new Error(`${t('stiklainis.failedToLoadRooms')}: ${roomsResponse.status}`);
      }

      const roomsData: ConferenceRoom[] = await roomsResponse.json();
      setRooms(roomsData);
      onRoomsLoadedRef.current?.(roomsData);

      if (!selectedRoom && roomsData.length > 0) {
        onRoomChangeRef.current?.(roomsData[0].id);
        setFormData((prev) => ({ ...prev, roomId: roomsData[0].id }));
      }

      const roomId = selectedRoom || roomsData[0]?.id;
      const { rangeStart, rangeEnd } =
        viewMode === 'month' ? getMonthGridRange(currentDate) : getWeekRangeBounds(currentDate);

      if (reservationsAbortRef.current) reservationsAbortRef.current.abort();
      const controller = new AbortController();
      reservationsAbortRef.current = controller;

      try {
        const all = await fetchAllReservationsInRange({
          getApiUrl,
          token,
          rangeStart,
          rangeEnd,
          roomId,
          signal: controller.signal,
        });
        setReservations(all);
      } catch (fetchErr: unknown) {
        if (fetchErr instanceof Error && fetchErr.name === 'AbortError') throw fetchErr;
        const statusMatch = fetchErr instanceof Error && fetchErr.message.match(/^HTTP (\d+)/);
        throw new Error(
          `${t('stiklainis.failedToLoadReservations')}${statusMatch ? `: ${statusMatch[1]}` : ''}`
        );
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;

      if (err instanceof Error && err.message.includes(t('stiklainis.failedToLoadRooms'))) {
        const mockRooms: ConferenceRoom[] = [{
          id: '550e8400-e29b-41d4-a716-446655440001',
          name: 'Stiklainis',
          capacity: 12,
          location: '2nd Floor, Main Building',
          amenities: 'Projector, Whiteboard, Video Conference, Coffee Machine',
          description: 'Pagrindinė konferencijų salė komandos susitikimams ir prezentacijoms',
        }];
        setRooms(mockRooms);
        onRoomsLoadedRef.current?.(mockRooms);
        setReservations([]);
        setError('Using demo data - API connection unavailable');
        if (!selectedRoom && mockRooms.length > 0) {
          onRoomChangeRef.current?.(mockRooms[0].id);
          setFormData((prev) => ({ ...prev, roomId: mockRooms[0].id }));
        }
      } else {
        setError(err instanceof Error ? err.message : t('stiklainis.error'));
      }
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
    }
  }, [token, selectedRoom, viewMode, currentDate, t]);

  useEffect(() => {
    loadData();
    return () => { reservationsAbortRef.current?.abort(); };
  }, [loadData]);

  const getMonthRange = () => `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  const getWeekRange = () => {
    const weekDays = getWeekDays(currentDate);
    const startDate = weekDays[0];
    const endDate = weekDays[6];
    const startMonth = monthNames[startDate.getMonth()];
    const endMonth = monthNames[endDate.getMonth()];
    const year = startDate.getFullYear();
    if (startMonth === endMonth) {
      return `${startMonth} ${startDate.getDate()}–${endDate.getDate()}, ${year}`;
    }
    return `${startMonth} ${startDate.getDate()} – ${endMonth} ${endDate.getDate()}, ${year}`;
  };

  const openCreateForm = (startDate: Date, startTime: Date, endTime: Date) => {
    setReservationDialogNotice(null);
    setEditingReservation(null);
    setFormData({
      ...emptyForm(),
      startDate,
      startTime,
      endTime,
      roomId: effectiveRoomId,
      attendeeCount: 1,
    });
    setOpenDialog(true);
  };

  const isPartOfSeries = (reservation: Reservation) =>
    Boolean(reservation.parentReservationId || reservation.isRecurring);

  const handleCreateReservation = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const slot = effectiveRoomId
      ? getNextAvailableSlot(today, reservations, effectiveRoomId, DAY_START_HOUR, DAY_END_HOUR)
      : null;

    if (slot) {
      openCreateForm(today, slot.startTime, slot.endTime);
    } else {
      const slotStart = getNext30MinSlotStart(now);
      const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), slotStart.getHours(), slotStart.getMinutes());
      openCreateForm(today, startTime, new Date(startTime.getTime() + 60 * 60 * 1000));
    }
  };

  const handleTimeSlotClick = (date: Date, timeSlot?: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const clickedIsToday = date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    const slot = effectiveRoomId
      ? getNextAvailableSlot(clickedIsToday ? today : date, reservations, effectiveRoomId, DAY_START_HOUR, DAY_END_HOUR)
      : null;

    if (slot) {
      openCreateForm(clickedIsToday ? today : date, slot.startTime, slot.endTime);
      return;
    }

    const dateForSlot = clickedIsToday ? today : date;
    if (clickedIsToday) {
      const slotStart = getNext30MinSlotStart(now);
      const startTime = new Date(dateForSlot);
      startTime.setHours(slotStart.getHours(), slotStart.getMinutes(), 0, 0);
      openCreateForm(dateForSlot, startTime, new Date(startTime.getTime() + 60 * 60 * 1000));
    } else if (timeSlot) {
      const [hour] = timeSlot.split(':').map(Number);
      const startTime = new Date(dateForSlot);
      startTime.setHours(hour, 0, 0, 0);
      const endTime = new Date(dateForSlot);
      endTime.setHours(hour + 1, 0, 0, 0);
      openCreateForm(dateForSlot, startTime, endTime);
    } else {
      const startTime = new Date(dateForSlot);
      startTime.setHours(9, 0, 0, 0);
      openCreateForm(dateForSlot, startTime, new Date(dateForSlot.getFullYear(), dateForSlot.getMonth(), dateForSlot.getDate(), 10, 0));
    }
  };

  const handleDayClick = (day: Date, e: React.MouseEvent<HTMLDivElement>) => {
    const totalHours = DAY_END_HOUR - DAY_START_HOUR;
    const totalHeight = totalHours * HOUR_ROW_HEIGHT;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutesIntoDay = Math.max(0, Math.min(totalHours * 60, (y / totalHeight) * (totalHours * 60)));
    const hour = Math.floor(minutesIntoDay / 60) + DAY_START_HOUR;
    handleTimeSlotClick(day, `${String(hour).padStart(2, '0')}:00`);
  };

  const handleEditReservation = (reservation: Reservation) => {
    setReservationDialogNotice(null);
    setEditingReservation(reservation);
    const startDate = new Date(reservation.startTime);
    const endDate = new Date(reservation.endTime);
    setFormData({
      title: reservation.title,
      description: reservation.description || '',
      startDate,
      startTime: startDate,
      endTime: endDate,
      roomId: reservation.room.id,
      attendeeCount: reservation.attendeeCount ?? '',
      isRecurring: false,
      recurringPattern: 'weekly',
      recurringWeeks: 4,
    });
    setOpenDialog(true);
  };

  const handleReservationClick = (reservation: Reservation, isOwn: boolean) => {
    const canManage = hasPermission('stiklainis.manage') || hasPermission('rooms.manage');
    if (isOwn || canManage) {
      handleEditReservation(reservation);
    } else {
      setInfoReservation(reservation);
      setInfoDialogOpen(true);
    }
  };

  const handleSaveReservation = async () => {
    try {
      if (!token) {
        setReservationDialogNotice({ type: 'error', text: t('stiklainis.notLoggedIn') });
        return;
      }

      const startDateTime = new Date(formData.startDate);
      startDateTime.setHours(formData.startTime.getHours(), formData.startTime.getMinutes(), 0, 0);
      const endDateTime = new Date(formData.startDate);
      endDateTime.setHours(formData.endTime.getHours(), formData.endTime.getMinutes(), 0, 0);

      const attendeeCountValue = typeof formData.attendeeCount === 'string'
        ? parseInt(formData.attendeeCount, 10)
        : formData.attendeeCount;

      const reservationData: Record<string, unknown> = {
        title: formData.title,
        description: formData.description,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        roomId: formData.roomId,
        attendeeCount: Number.isFinite(attendeeCountValue as number) && (attendeeCountValue as number) >= 1 ? attendeeCountValue : 1,
      };

      if (!editingReservation && formData.isRecurring && formData.recurringWeeks >= 1) {
        const endDay = recurringEndFromWeeks(
          formData.startDate,
          formData.recurringPattern,
          formData.recurringWeeks
        );
        endDay.setHours(12, 0, 0, 0);
        reservationData.isRecurring = true;
        reservationData.recurringPattern = formData.recurringPattern;
        reservationData.recurringEndDate = endDay.toISOString();
      }

      const url = editingReservation
        ? getApiUrl(`/reservations/${editingReservation.id}`)
        : getApiUrl('/reservations');
      const method = editingReservation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = t('stiklainis.failedToSaveReservation');
        const isTimeSlotError = (s: string) => {
          const lower = (s || '').toLowerCase();
          return lower.includes('not available') || lower.includes('time slot') || lower.includes('conflict') || lower.includes('overlap') || lower.includes('taken') || lower.includes('already booked');
        };
        try {
          const errorData = JSON.parse(errorText);
          const errStr = errorData.error || errorData.message || (Array.isArray(errorData.errors) && errorData.errors[0]?.msg) || '';
          if (Array.isArray(errorData.conflictDates) && errorData.conflictDates.length > 0) {
            errorMessage = t('stiklainis.seriesConflictDates', 'Užimtos datos: {{dates}}', {
              dates: errorData.conflictDates.join(', '),
            });
          } else if (errStr) {
            if (isTimeSlotError(errStr)) errorMessage = t('stiklainis.timeSlotTaken');
            else if (errStr.toLowerCase().includes('daily') && errStr.toLowerCase().includes('1 week')) {
              errorMessage = t(
                'stiklainis.repeatDailyLimitHelp',
                'Kasdien kartoti galima tik 1 savaitę (7 dienos).'
              );
            }
            else if (errStr.toLowerCase().includes('2 months') || errStr.toLowerCase().includes('recurring end')) {
              errorMessage = t(
                'stiklainis.repeatTooLong',
                'Kartojimą galima planuoti ne ilgiau kaip 2 mėnesius.'
              );
            }
            else if (errStr.toLowerCase().includes('past')) errorMessage = t('stiklainis.cannotBookPast');
            else if (errStr.toLowerCase().includes('capacity')) errorMessage = errStr;
            else if (errStr.toLowerCase().includes('title') && errStr.toLowerCase().includes('required')) errorMessage = t('stiklainis.titleRequired');
            else errorMessage = errStr;
          }
        } catch {
          if (errorText && errorText.length < 200 && isTimeSlotError(errorText)) errorMessage = t('stiklainis.timeSlotTaken');
          else if (errorText && errorText.length < 200) errorMessage = errorText;
        }
        setReservationDialogNotice({ type: 'error', text: errorMessage });
        return;
      }

      setOpenDialog(false);
      setReservationDialogNotice(null);
      loadData();
    } catch (err: unknown) {
      setReservationDialogNotice({
        type: 'error',
        text: err instanceof Error ? err.message : t('stiklainis.failedToSaveReservation'),
      });
    }
  };

  const requestCancelReservation = (reservationId: string, reservation?: Reservation | null) => {
    const target =
      reservation ||
      reservations.find((r) => r.id === reservationId) ||
      (editingReservation?.id === reservationId ? editingReservation : null);
    if (target && isPartOfSeries(target)) {
      setDeleteSeriesPrompt({ open: true, reservationId });
      return;
    }
    void performCancelReservation(reservationId, false);
  };

  const performCancelReservation = async (reservationId: string, deleteSeries: boolean) => {
    try {
      const qs = deleteSeries ? '?deleteSeries=true' : '';
      const response = await fetch(getApiUrl(`/reservations/${reservationId}${qs}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = t('stiklainis.failedToCancelReservation');
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            if (errorData.error.includes('past') || errorData.error.includes('current')) {
              errorMessage = t('stiklainis.cannotDeletePastReservation');
            } else {
              errorMessage = errorData.error;
            }
          }
        } catch { /* use default */ }
        throw new Error(errorMessage);
      }

      setDeleteSeriesPrompt({ open: false, reservationId: null });
      setOpenDialog(false);
      setInfoDialogOpen(false);
      setReservationDialogNotice(null);
      loadData();
    } catch (err: unknown) {
      setDeleteSeriesPrompt({ open: false, reservationId: null });
      setError(err instanceof Error ? err.message : t('stiklainis.failedToCancelReservation'));
    }
  };

  /** @deprecated use requestCancelReservation — kept name for callers */
  const handleCancelReservation = (reservationId: string) => {
    requestCancelReservation(reservationId);
  };

  const canDeleteReservation = (reservation: Reservation) =>
    reservation.user.id === user?.id ||
    hasPermission('stiklainis.manage') ||
    hasPermission('rooms.manage');

  const handleFormChange = (data: Partial<ReservationFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  return {
    user,
    rooms,
    loading,
    error,
    setError,
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    reservations,
    effectiveRoomId,
    dayNames,
    dateRangeLabel: viewMode === 'week' ? getWeekRange() : getMonthRange(),
    openDialog,
    setOpenDialog,
    editingReservation,
    formData,
    reservationDialogNotice,
    setReservationDialogNotice,
    infoDialogOpen,
    setInfoDialogOpen,
    infoReservation,
    calendarMenuAnchor,
    setCalendarMenuAnchor,
    calendarMenuReservation,
    setCalendarMenuReservation,
    handleCreateReservation,
    handleTimeSlotClick,
    handleDayClick,
    handleReservationClick,
    handleSaveReservation,
    handleCancelReservation,
    requestCancelReservation,
    performCancelReservation,
    deleteSeriesPrompt,
    setDeleteSeriesPrompt,
    isPartOfSeries,
    handleEditReservation,
    handleFormChange,
    canDeleteReservation,
    handlePrevious: () => {
      const d = new Date(currentDate);
      if (viewMode === 'week') d.setDate(d.getDate() - 7);
      else d.setMonth(d.getMonth() - 1);
      setCurrentDate(d);
    },
    handleNext: () => {
      const d = new Date(currentDate);
      if (viewMode === 'week') d.setDate(d.getDate() + 7);
      else d.setMonth(d.getMonth() + 1);
      setCurrentDate(d);
    },
    handleToday: () => setCurrentDate(new Date()),
    dayAgendaDate,
    setDayAgendaDate,
    openDayAgenda: (day: Date) => {
      const d = new Date(day);
      d.setHours(0, 0, 0, 0);
      setDayAgendaDate(d);
    },
    closeDayAgenda: () => setDayAgendaDate(null),
    selectedMobileDay,
    setSelectedMobileDay,
    onRoomChange,
  };
}

export type StiklainisCalendarState = ReturnType<typeof useStiklainisCalendar>;
